import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ImageItem, ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItem, VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {ImageSlideItem, ImageSlideItemObject} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {AddTranscriptOpts, ImageData, TempImageData, VideoData} from '@Libraries/add-media-library';
import {isTempImageData, ElementDataType, getGettyAssetClassification} from '@Libraries/add-media-library';
import {prepImageItemObjectFromImageData, prepVideoItemObjectFromVideoData} from '@PosterWhiteboard/items/item/element-data-to-item.library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {loadImageAsync} from '@Utils/image.util';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {getFilenameForVideo, getRepoVideoURL} from '@Libraries/user-video-library';
import {loadVideo} from '@Utils/video.util';
import type {SlideshowItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {SlideItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {CropperPanelApplyResponse} from '@Panels/cropper-panel';
import type {VideoCropperPanelApplyResponse} from '@Panels/video-cropper-panel/video-cropper-panel.types';
import {getUniqueString} from '@Utils/string.util';
import type {ItemData, NewImageItemData, NewTempImageItemData, NewTranscriptData} from '@PosterWhiteboard/page/add-item.class';
import {getImageScreenURL} from '@Libraries/image-item.library';
import type {MaskingShape, MaskingShapeObject} from '@PosterWhiteboard/classes/masking/masking-shape.class';
import type {MaskingText, MaskingTextObject} from '@PosterWhiteboard/classes/masking/masking-text.class';
import {DEFAULT_SLIDE_DURATION} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import type {DeepPartial} from '@/global';
import {PMWStockImageSource} from '@Libraries/pmw-stock-media-library.types';
import {convertFabricShadowObjectToShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';

export class ReplaceItem {
  public page: Page;

  private static readonly ASPECT_RATIO_TOLERANCE = 0.01;
  private static readonly IMAGE_ASPECT_RATIO_RELATIVE_TOLERANCE = 0.15;

  public constructor(page: Page) {
    this.page = page;
  }

  public async replaceTranscript(uidOfTranscriptToReplace: string, uidForTranscriptToAdd: string, itemToReplaceWith: NewTranscriptData, opts: AddTranscriptOpts): Promise<void> {
    const transcriptItemToReplace = this.page.items.getItem(uidOfTranscriptToReplace);
    if (!transcriptItemToReplace) {
      throw new Error(`Could not find existing transcript item with uid ${uidOfTranscriptToReplace}`);
    }
    if (!transcriptItemToReplace.isTranscript()) {
      throw new Error('Expected a transcript item, but got something else');
    }

    itemToReplaceWith.selectedSubtitleTemplateId = transcriptItemToReplace.getAnySubtitle().selectedTemplateId;
    itemToReplaceWith.styles = {
      sentenceTextStyles: transcriptItemToReplace.getAnySubtitle().sentenceTextStyles,
      activeWordTextStyles: transcriptItemToReplace.getAnySubtitle().activeWordTextStyles,
      shadow: convertFabricShadowObjectToShadowOrGlowOptionsObject(transcriptItemToReplace.getAnySubtitle().toObject().shadow, transcriptItemToReplace.getScaleForShadow()),
      backgroundFill: transcriptItemToReplace.getAnySubtitle().backgroundFill,
      backgroundBorderRadius: transcriptItemToReplace.getAnySubtitle().backgroundBorderRadius,
      animationStyle: transcriptItemToReplace.getAnySubtitle().animationStyle,
    };

    this.page.poster.deleteItemById(uidOfTranscriptToReplace, false);

    await this.page.items.addItems.addTranscriptItem(itemToReplaceWith, {
      optionsToAdd: {
        left: transcriptItemToReplace.fabricObject.left,
        top: transcriptItemToReplace.fabricObject.top,
        width: transcriptItemToReplace.fabricObject.width,
        height: transcriptItemToReplace.fabricObject.height,
        lockMovementX: transcriptItemToReplace.fabricObject.lockMovementX,
        angle: transcriptItemToReplace.fabricObject.angle,
        ...opts.optionsToAdd,
        uid: uidForTranscriptToAdd,
      },
      selectOnAdd: opts.selectOnAdd,
    });
  }

  public async replaceMedia(itemToReplaceWith: ItemData, activeItem: ImageItem | VideoItem | ImageSlideItem | VideoSlideItem, allowAspectRatioTolerance = false): Promise<void> {
    const {allowed, droppedTypes} = this.page.poster.filterByGettyLimits([itemToReplaceWith], getGettyAssetClassification, {excludeItemUids: new Set([activeItem.uid])});
    getPosterUi().notifyGettyLimitReached(droppedTypes);
    if (allowed.length === 0) {
      return;
    }

    const scaledWidth = activeItem.fabricObject.getScaledWidth();
    const scaledHeight = activeItem.fabricObject.getScaledHeight();

    if (itemToReplaceWith.type === ElementDataType.IMAGE) {
      return this.replaceWithImage(
        {
          scaledWidth,
          scaledHeight,
          maintainAspectRatioForScaledDimension: true,
          ...itemToReplaceWith,
        },
        activeItem,
        allowAspectRatioTolerance
      );
    }
    if (itemToReplaceWith.type === ElementDataType.VIDEO) {
      return this.replaceWithVideo(
        {
          scaledWidth,
          scaledHeight,
          maintainAspectRatioForScaledDimension: true,
          ...itemToReplaceWith,
        },
        activeItem
      );
    }

    throw new Error('unexpected type received in replace media');
  }

  public async replaceMediaSlide(itemToReplaceWith: ItemData, activeSlide: ImageSlideItem | VideoSlideItem, activeItem: SlideshowItem): Promise<void> {
    const {allowed, droppedTypes} = this.page.poster.filterByGettyLimits([itemToReplaceWith], getGettyAssetClassification, {excludeItemUids: new Set([activeSlide.uid])});
    getPosterUi().notifyGettyLimitReached(droppedTypes);
    if (allowed.length === 0) {
      return;
    }

    if (itemToReplaceWith.type === ElementDataType.IMAGE) {
      return this.replaceSlideWithImageSlide(itemToReplaceWith, activeSlide, activeItem);
    }
    if (itemToReplaceWith.type === ElementDataType.VIDEO) {
      return this.replaceSlideWithVideoSlide(itemToReplaceWith, activeSlide, activeItem);
    }

    throw new Error('unexpected type received in replace media slide');
  }

  public async replaceSlideWithVideoSlide(item: VideoData, activeSlide: ImageSlideItem | VideoSlideItem, activeItem: SlideshowItem): Promise<void> {
    const itemToAdd: Partial<VideoSlideItem> = {};
    const uid = getUniqueString();
    itemToAdd.hashedFilename = item.hashedFilename;
    itemToAdd.duration = item.duration;
    itemToAdd.endTime = item.duration;
    itemToAdd.frameRate = item.frameRate;
    itemToAdd.videoSource = item.source;
    // TODO: transparency
    itemToAdd.startTime = 0;
    itemToAdd.fileExtension = item.extension;
    itemToAdd.slideDuration = item.duration;

    itemToAdd.uid = uid;

    const {slidesOrder, slidesHashMap} = activeItem.slides.toObject();

    const visuals = {};
    if (activeSlide.gitype === ITEM_TYPE.VIDEOSLIDE) {
      this.copyVisuals(visuals, activeSlide);
    }

    for (let index = 0; index < slidesOrder.length; index += 1) {
      if (slidesOrder[index] === activeSlide.uid) {
        slidesOrder[index] = uid;
      }
    }

    slidesHashMap[uid] = {
      ...visuals,
      gitype: ITEM_TYPE.VIDEOSLIDE,
      videoSource: itemToAdd.fileExtension,
      fileExtension: itemToAdd.fileExtension,
      hashedFilename: itemToAdd.hashedFilename,
      duration: itemToAdd.duration,
      frameRate: itemToAdd.frameRate,
      uid: itemToAdd.uid,
      slideDuration: itemToAdd.slideDuration,
    } as SlideItemObject;

    delete slidesHashMap[activeSlide.uid];

    showLoading('replaceWithVideoSlide');
    activeItem
      .updateFromObject(
        {
          slides: {
            slidesOrder,
            slidesHashMap,
          },
        },
        {}
      )
      .then((): void => {})
      .catch((): void => {})
      .finally((): void => {
        hideLoading('replaceWithVideoSlide');
      });
  }

  public async replaceSlideWithImageSlide(item: ImageData | TempImageData, activeSlide: ImageSlideItem | VideoSlideItem, activeItem: SlideshowItem): Promise<void> {
    const itemToAdd: Partial<ImageSlideItemObject> = {};
    const uid = 'uid' in item ? item.uid : getUniqueString();
    itemToAdd.imageSource = item.source;
    itemToAdd.uid = uid;

    const {slidesOrder, slidesHashMap} = activeItem.slides.toObject();

    const visuals = {};
    if (activeSlide.gitype === ITEM_TYPE.IMAGESLIDE) {
      this.copyVisuals(visuals, activeSlide);
    }

    for (let index = 0; index < slidesOrder.length; index += 1) {
      if (slidesOrder[index] === activeSlide.uid) {
        slidesOrder[index] = uid;
      }
    }
    slidesHashMap[uid] = {
      ...visuals,
      gitype: ITEM_TYPE.IMAGESLIDE,
      imageSource: itemToAdd.imageSource,
      uid,
      slideDuration: DEFAULT_SLIDE_DURATION,
    } as SlideItemObject;

    if (isTempImageData(item)) {
      slidesHashMap[uid] = {
        ...slidesHashMap[uid],
        uploadingImageData: {
          tempUploadingImageUID: uid,
          dataUrl: item.dataUrl,
          uploadStartTime: new Date().getTime(),
        },
      };
    } else {
      slidesHashMap[uid] = {
        ...slidesHashMap[uid],
        fileExtension: item.extension,
        hashedFilename: item.hashedFilename,
        isUpscaled: item.isUpscaled ?? false,
      };
    }

    delete slidesHashMap[activeSlide.uid];

    showLoading('replaceWithImageSlide');
    await activeItem
      .updateFromObject({
        slides: {
          slidesOrder,
          slidesHashMap,
        },
      })
      .then((): void => {})
      .catch((): void => {})
      .finally((): void => {
        hideLoading('replaceWithImageSlide');
      });
  }

  private copyVisuals(itemToAdd: DeepPartial<ImageItemObject | VideoItemObject>, activeItem: ImageItem | VideoItem): void {
    itemToAdd.border = activeItem.border.toObject();
    itemToAdd.effects = activeItem.effects.toObject();
    itemToAdd.lockMovementX = activeItem.fabricObject.lockMovementX;
    itemToAdd.lockMovementY = activeItem.fabricObject.lockMovementY;
    itemToAdd.lockRotation = activeItem.fabricObject.lockRotation;
    itemToAdd.lockScalingX = activeItem.fabricObject.lockScalingX;
    itemToAdd.lockScalingY = activeItem.fabricObject.lockScalingY;
    itemToAdd.shadow = activeItem.fabricObject.shadow?.toObject();
    itemToAdd.alpha = activeItem.fabricObject.opacity;
  }

  private copyMaskingItems(itemToAdd: DeepPartial<ImageItemObject>, itemToAddWidth: number, itemToAddHeight: number, activeItem: ImageItem): void {
    if (!activeItem.masking.maskingItem || activeItem.masking.maskingItem.isFreehand()) {
      return;
    }

    itemToAdd.masking = {
      ...activeItem.masking.toObject(),
      maskingItem: this.getCopiedMaskingItemObject(activeItem.masking.maskingItem, itemToAddWidth, itemToAddHeight),
    };
  }

  private getCopiedMaskingItemObject(maskingItem: MaskingShape | MaskingText, itemToAddWidth: number, itemToAddHeight: number): MaskingShapeObject | MaskingTextObject {
    const heightRatio = itemToAddHeight / maskingItem.imageHeight;
    const widthRatio = itemToAddWidth / maskingItem.imageWidth;
    const smallerRatio = widthRatio < heightRatio ? widthRatio : heightRatio;

    return {
      ...maskingItem.toObject(),
      imageWidth: itemToAddWidth,
      imageHeight: itemToAddHeight,
      top: maskingItem.top * smallerRatio,
      left: maskingItem.left * smallerRatio,
      scaleX: maskingItem.scaleX * smallerRatio,
      scaleY: maskingItem.scaleY * smallerRatio,
    };
  }

  public async replaceWithImage(
    item: NewImageItemData | NewTempImageItemData,
    activeItem: ImageItem | VideoItem | ImageSlideItem | VideoSlideItem,
    allowAspectRatioTolerance = false
  ): Promise<void> {
    const zIndex = activeItem.fabricObject.getZIndex();
    const itemToAdd: DeepPartial<ImageItemObject> = prepImageItemObjectFromImageData(item);
    itemToAdd.top = activeItem.fabricObject.getY();
    itemToAdd.left = activeItem.fabricObject.getX();
    itemToAdd.angle = activeItem.fabricObject.angle;
    if (activeItem.isBackground) {
      itemToAdd.isBackground = true;
      itemToAdd.onPageStartTime = activeItem.onPageStartTime;
      itemToAdd.onPageEndTime = activeItem.getPageEndTime();
    }

    if ('extension' in item && item.extension === 'svg') {
      this.page.poster.deleteItemById(activeItem.uid, false);
      this.page.activeSelection.clearSelection();
      void this.page.items.addItems.addImageItem(item, itemToAdd, {
        zIndex,
      });
      return;
    }

    let replacingAspectRatio = 0;
    if (activeItem.fabricObject.scaleX) {
      replacingAspectRatio = (activeItem.fabricObject.width * activeItem.fabricObject.scaleX) / (activeItem.fabricObject.height * activeItem.fabricObject.scaleY);
    } else {
      replacingAspectRatio = activeItem.fabricObject.width / activeItem.fabricObject.height;
    }

    if (activeItem.isImage()) {
      this.copyVisuals(itemToAdd, activeItem);
    }

    if (item) showLoading('LoadingImage');
    let imageSource;
    if ('hashedFilename' in item && 'extension' in item) {
      imageSource = getImageScreenURL(item.hashedFilename, item.extension, item.source);
    } else if (item.dataUrl) {
      imageSource = item.dataUrl;
    }
    if (!imageSource) {
      console.error('no image source found');
      return;
    }
    await loadImageAsync(imageSource)
      .then(async (img) => {
        this.page.poster.deleteItemById(activeItem.uid, false);
        this.page.activeSelection.clearSelection();

        const uid = 'uid' in item && item.uid ? item.uid : getUniqueString();

        let cropOnAdd = false;
        let maskOnAdd = false;
        if (activeItem.isImage()) {
          if (this.doesImageReplacementNeedCrop(replacingAspectRatio, img.width / img.height, allowAspectRatioTolerance)) {
            cropOnAdd = true;
          }

          if (activeItem.masking.maskingItem && !activeItem.masking.maskingItem.isFreehand()) {
            this.copyMaskingItems(itemToAdd, img.width, img.height, activeItem);
            cropOnAdd = false;
            maskOnAdd = true;
          }
        }

        if (activeItem.isVideo()) {
          if (Math.abs(replacingAspectRatio - img.width / img.height) > ReplaceItem.ASPECT_RATIO_TOLERANCE) {
            cropOnAdd = true;
          }
        }

        if (cropOnAdd) {
          if (item) hideLoading('LoadingImage');
          const cropperPanelResponse = await this.openCropModalAfterReplace(item, replacingAspectRatio);
          if (item) showLoading('LoadingImage');
          if (cropperPanelResponse) {
            itemToAdd.cropData = {
              cropped: true,
              ...cropperPanelResponse.cropData,
            };
            itemToAdd.removeBackground = {
              isBackgroundRemoved: cropperPanelResponse.isBackgroundRemoved,
            };
          }
        }

        await this.page.items.addItems.addImageItem(
          item,
          {uid, ...itemToAdd},
          {
            zIndex,
          }
        );

        if (maskOnAdd) {
          await this.openMaskingModalAfterReplace(uid);
        }
      })
      .catch(() => {
        this.errorModalWhileReplacingItem();
      })
      .finally(() => {
        hideLoading('LoadingImage');
      });
  }

  doesImageReplacementNeedCrop(currentAspectRatio: number, newAspectRatio: number, allowAspectRatioTolerance: boolean): boolean {
    if (allowAspectRatioTolerance) {
      return Math.abs(currentAspectRatio - newAspectRatio) / currentAspectRatio > ReplaceItem.IMAGE_ASPECT_RATIO_RELATIVE_TOLERANCE;
    }
    return currentAspectRatio !== newAspectRatio;
  }

  private async openMaskingModalAfterReplace(itemUID: string): Promise<void> {
    const addedItem = this.page.poster.getItemForId(itemUID) as ImageItem;
    await getPosterUi().onOpenMaskingModal(addedItem);
  }

  private async openCropModalAfterReplace(addedItem: NewImageItemData | NewTempImageItemData, aspectRatio: number): Promise<CropperPanelApplyResponse | undefined> {
    return new Promise((resolve) => {
      getPosterUi().openCropperModal({
        onApply: resolve,
        onClose: (): void => {
          resolve(undefined);
        },
        aspectRatio,
        dataUrl: 'dataUrl' in addedItem ? addedItem.dataUrl : undefined,
        imageHashedFilename: 'hashedFilename' in addedItem ? addedItem.hashedFilename : undefined,
        imageSource: addedItem.source,
        showCancelButton: true,
        showRemoveBackgroundOption: addedItem.source !== PMWStockImageSource.PMW_STOCK_IMAGE,
        cancelButtonCtaText: window.i18next.t('pmwjs_use_full_size'),
        applyButtonCtaText: window.i18next.t('pmwjs_crop_and_add'),
      });
    });
  }

  private async openVideoCropModalAfterReplace(videoSrc: string, aspectRatio: number): Promise<VideoCropperPanelApplyResponse | undefined> {
    return new Promise((resolve) => {
      getPosterUi().openVideoCropperModal({
        onApply: resolve,
        onClose: (): void => {
          resolve(undefined);
        },
        videoSrc,
        aspectRatio,
        applyButtonCtaText: window.i18next.t('pmwjs_crop_and_add'),
      });
    });
  }

  public async replaceWithVideo(item: VideoData, activeItem: ImageItem | VideoItem | ImageSlideItem | VideoSlideItem): Promise<void> {
    const zIndex = activeItem.fabricObject.getZIndex();
    const itemToAdd: Partial<VideoItemObject> = prepVideoItemObjectFromVideoData(item);
    itemToAdd.left = activeItem.fabricObject.getX();
    itemToAdd.top = activeItem.fabricObject.getY();
    itemToAdd.angle = activeItem.fabricObject.angle;
    if (activeItem.isBackground) {
      itemToAdd.isBackground = true;
      itemToAdd.onPageStartTime = activeItem.onPageStartTime;
      itemToAdd.onPageEndTime = activeItem.getPageEndTime();
    }

    let replacingAspectRatio = 0;
    if (activeItem.fabricObject.scaleX) {
      replacingAspectRatio = (activeItem.fabricObject.width * activeItem.fabricObject.scaleX) / (activeItem.fabricObject.height * activeItem.fabricObject.scaleY);
    } else {
      replacingAspectRatio = activeItem.fabricObject.width / activeItem.fabricObject.height;
    }

    showLoading('loadingVideo');
    const fileNameForVideo = getFilenameForVideo(item.hashedFilename, item.extension, item.hasTransparency);
    const videoSource = getRepoVideoURL(fileNameForVideo, this.page.poster.isHighRes);

    if (activeItem.gitype === ITEM_TYPE.VIDEO) {
      this.copyVisuals(itemToAdd, activeItem);
    }

    loadVideo(videoSource)
      .then(async (video) => {
        const newVideoAspectRatio = video.videoWidth / video.videoHeight;
        const cropOnAdd = Math.abs(replacingAspectRatio - newVideoAspectRatio) > ReplaceItem.ASPECT_RATIO_TOLERANCE;

        if (cropOnAdd) {
          hideLoading('loadingVideo');
          const videoCropperResponse = await this.openVideoCropModalAfterReplace(videoSource, replacingAspectRatio);
          showLoading('loadingVideo');

          if (videoCropperResponse) {
            itemToAdd.cropData = {
              cropped: true,
              ...videoCropperResponse.cropData,
            };
          }
        }

        itemToAdd.width = video.width;
        itemToAdd.height = video.height;

        this.page.poster.deleteItemById(activeItem.uid, false);
        this.page.activeSelection.clearSelection();
        void this.page.items.addItems.addVideoItem(item, itemToAdd, {zIndex});
      })
      .catch(() => {
        this.errorModalWhileReplacingItem();
      })
      .finally(() => {
        hideLoading('loadingVideo');
      });
  }

  public errorModalWhileReplacingItem(): void {
    getPosterUi().openErrorModal({
      message: window.i18next.t('pmwjs_general_ajax_error'),
      log: false,
    });
  }
}

export const onReplaceMedia = (itemToReplaceWith: ItemData): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }
  const activeItem = currentPage.getSelectedItems();

  if (!activeItem || activeItem.length !== 1) {
    return;
  }

  if (!activeItem[0].isImage() && !activeItem[0].isVideo()) {
    return;
  }

  void currentPage.items.replaceItems.replaceMedia(itemToReplaceWith, activeItem[0]);
};

export const onReplaceMediaSlide = (itemToReplaceWith: ItemData): void => {
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!currentPage) {
    return;
  }

  const activeItems = currentPage.getSelectedItems();

  if (!activeItems || activeItems.length !== 1) {
    return;
  }

  const activeItem = activeItems[0];

  if (!activeItem.isSlideshow()) {
    return;
  }

  const activeSlide = activeItem.getSelectedSlide();

  if (!activeSlide.isMediaSlide()) {
    return;
  }

  void currentPage.items.replaceItems.replaceMediaSlide(itemToReplaceWith, activeSlide, activeItem);
};
